import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ErrorService } from '../../error';

const JSON_API_MEDIA_TYPE = 'application/vnd.api+json';

/**
 * What a subscription is to: an application topic, a national culture or a
 * published show, identified by its slug — `{type: 'topics', id: 'culturettt'}`.
 */
export interface SubscriptionCriterion {
  type: 'topics' | 'cultures' | 'shows';
  id: string;
}

/** How a subscription tells the reader about each new item: in the app, by email, or both. */
export interface SubscriptionSwitches {
  inApp: boolean;
  email: boolean;
}

export interface Subscription extends SubscriptionSwitches {
  id: string;
  criterion: SubscriptionCriterion | null;
  createdAt: Date | null;
}

/** How often the news service emails its digest. */
export type NewsDigestCadence = 'daily' | 'weekly' | 'monthly';

export const NEWS_DIGEST_CADENCES: NewsDigestCadence[] = ['daily', 'weekly', 'monthly'];

/** The reader's subscription to the news service: every news item, or every big one, in a digest. */
export interface NewsServiceSubscription {
  cadence: NewsDigestCadence;
  bigNewsOnly: boolean;
}

type SubscriptionResourceObject = {
  type: 'subscriptions';
  id: string;
  attributes: { in_app: boolean; email: boolean; created_at: string | null };
  relationships?: { criterion?: { data: SubscriptionCriterion | null } };
};

type NewsSubscriptionResourceObject = {
  type: 'news-subscriptions';
  id: string;
  attributes: { cadence: NewsDigestCadence; big_news_only: boolean };
};

type ShowResourceObject = {
  type: 'shows';
  id: string;
  attributes: { slug: string; name: string };
};

/**
 * The reader's subscriptions, through `/api/v1/subscriptions`, and their
 * subscription to the news service, through `/api/v1/news-subscription`.
 * Transport and normalisation only.
 */
@Injectable({
  providedIn: 'root',
})
export class SubscriptionService {
  private http = inject(HttpClient);
  private errorService = inject(ErrorService);

  private readonly headers = new HttpHeaders({ Accept: JSON_API_MEDIA_TYPE, 'Content-Type': JSON_API_MEDIA_TYPE });

  getSubscriptions(): Observable<Subscription[]> {
    return this.http.get<{ data: SubscriptionResourceObject[] }>('api/v1/subscriptions', { headers: this.headers }).pipe(
      map((document) => document.data.map((resource) => this.normalise(resource))),
      catchError((error) => this.handleError(error)),
    );
  }

  /** Subscribe with both switches on. */
  subscribe(criterion: SubscriptionCriterion): Observable<Subscription> {
    const body = {
      data: {
        type: 'subscriptions',
        attributes: { in_app: true, email: true },
        relationships: { criterion: { data: criterion } },
      },
    };

    return this.http.post<{ data: SubscriptionResourceObject }>('api/v1/subscriptions', body, { headers: this.headers }).pipe(
      map((document) => this.normalise(document.data)),
      catchError((error) => this.handleError(error)),
    );
  }

  changeSwitches(id: string, switches: Partial<SubscriptionSwitches>): Observable<Subscription> {
    const attributes: { in_app?: boolean; email?: boolean } = {};
    if (switches.inApp !== undefined) {
      attributes.in_app = switches.inApp;
    }
    if (switches.email !== undefined) {
      attributes.email = switches.email;
    }
    const body = { data: { type: 'subscriptions', id, attributes } };

    return this.http.patch<{ data: SubscriptionResourceObject }>('api/v1/subscriptions/' + encodeURIComponent(id), body, { headers: this.headers }).pipe(
      map((document) => this.normalise(document.data)),
      catchError((error) => this.handleError(error)),
    );
  }

  unsubscribe(id: string): Observable<void> {
    return this.http.delete<void>('api/v1/subscriptions/' + encodeURIComponent(id), { headers: this.headers }).pipe(
      catchError((error) => this.handleError(error)),
    );
  }

  /** The reader's subscription to the news service, or null when they have none. */
  getNewsService(): Observable<NewsServiceSubscription | null> {
    return this.http.get<{ data: NewsSubscriptionResourceObject }>('api/v1/news-subscription', { headers: this.headers }).pipe(
      map((document) => this.normaliseNewsService(document.data)),
      catchError((error: HttpErrorResponse) => (error.status === 404 ? of(null) : this.handleError(error))),
    );
  }

  /** Take the news service, or change it. */
  putNewsService(subscription: NewsServiceSubscription): Observable<NewsServiceSubscription> {
    const body = {
      data: {
        type: 'news-subscriptions',
        attributes: { cadence: subscription.cadence, big_news_only: subscription.bigNewsOnly },
      },
    };

    return this.http.put<{ data: NewsSubscriptionResourceObject }>('api/v1/news-subscription', body, { headers: this.headers }).pipe(
      map((document) => this.normaliseNewsService(document.data)),
      catchError((error) => this.handleError(error)),
    );
  }

  leaveNewsService(): Observable<void> {
    return this.http.delete<void>('api/v1/news-subscription', { headers: this.headers }).pipe(
      catchError((error) => this.handleError(error)),
    );
  }

  /** Published shows' names by slug, in the reader's language, to name show subscriptions. */
  getShowNames(): Observable<Map<string, string>> {
    return this.http.get<{ data: ShowResourceObject[] }>('api/v1/shows', { headers: this.headers }).pipe(
      map((document) => new Map(document.data.map((show) => [show.attributes.slug, show.attributes.name]))),
      catchError((error) => this.handleError(error)),
    );
  }

  private normalise(resource: SubscriptionResourceObject): Subscription {
    return {
      id: resource.id,
      inApp: resource.attributes.in_app,
      email: resource.attributes.email,
      criterion: resource.relationships?.criterion?.data ?? null,
      createdAt: resource.attributes.created_at ? new Date(resource.attributes.created_at) : null,
    };
  }

  private normaliseNewsService(resource: NewsSubscriptionResourceObject): NewsServiceSubscription {
    return { cadence: resource.attributes.cadence, bigNewsOnly: resource.attributes.big_news_only };
  }

  private handleError(error: HttpErrorResponse) {
    return throwError(() => this.errorService.normaliseHttpError(error, 'error.something_went_wrong'));
  }
}
